import { useMutation, useQuery } from "@tanstack/react-query";
import { getApiClient } from "../api-client";

// Mirrors `BookCandidate` from `@plotlounge/shared/books` — the books/search
// endpoint returns search candidates from external providers, *not* persisted
// rows, so there is no DB id to send back.
export interface BookSearchResult {
  title: string;
  author: string;
  description?: string | null;
  coverUrl?: string | null;
  pageCount?: number | null;
  openLibraryId?: string | null;
  googleBooksId?: string | null;
  isbn?: string | null;
}

export interface MargauxPreview {
  intro: string | null;
  iceBreaker: string | null;
  hookLine: string | null;
}

export type BookRefInput =
  | { id: string }
  | {
      title: string;
      author: string;
      description?: string;
      coverUrl?: string;
    };

export interface SegmentInput {
  segmentNumber: number;
  title: string;
  description?: string;
  startPage?: number;
  endPage?: number;
  startChapter?: number;
  endChapter?: number;
}

export interface CreateRoomInput {
  name: string;
  book: BookRefInput;
  startDate: string;
  endDate: string;
  maxMembers?: number;
  hookLine?: string;
  segments?: SegmentInput[];
}

export interface CreateRoomResult {
  id: string;
  name: string;
  status: string;
}

export interface ParsedYamlRoom {
  name: string;
  book: BookRefInput;
  startDate: string;
  endDate: string;
  maxMembers: number | null;
  hookLine: string | null;
  segments: Array<{
    segmentNumber: number;
    title: string;
    description: string | null;
    startPage: number | null;
    endPage: number | null;
    startChapter: number | null;
    endChapter: number | null;
  }> | null;
}

export function useBookSearchQuery(query: string) {
  const trimmed = query.trim();
  return useQuery<BookSearchResult[]>({
    queryKey: ["staff", "book-search", trimmed],
    enabled: trimmed.length >= 2,
    queryFn: () =>
      getApiClient().get<BookSearchResult[]>(
        `/books/search?q=${encodeURIComponent(trimmed)}&limit=8`,
      ),
    staleTime: 30 * 1000,
  });
}

export function usePreviewMargauxMutation() {
  return useMutation<MargauxPreview, Error, { book: BookRefInput }>({
    mutationFn: (input) =>
      getApiClient().post<MargauxPreview>(
        "/admin/plotlounge/rooms/preview-margaux",
        input,
      ),
  });
}

export function useParseYamlMutation() {
  return useMutation<ParsedYamlRoom, Error, { yaml: string }>({
    mutationFn: (input) =>
      getApiClient().post<ParsedYamlRoom>(
        "/admin/plotlounge/rooms/parse-yaml",
        input,
      ),
  });
}

export function useCreateRoomMutation() {
  return useMutation<CreateRoomResult, Error, CreateRoomInput>({
    mutationFn: (input) =>
      getApiClient().post<CreateRoomResult>("/admin/plotlounge/rooms", input),
  });
}
